import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import {
    ApiService,
    GraphQLResponse,
    Mk2Request,
    Mk2Response
} from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';
import {
    EnumInput,
    PayablesInput,
    PayablesResponse,
    PayablesSortColumn,
    PendingGroup,
    Query,
    SortDirection,
    SupplierPendingGroupItem
} from '../../model/models';
import { calculateAmount, deDuplicatePendingCreditMemos } from '../../helpers/credit-memo-mapping';

export class PendingGroupRequest extends Mk2Request {
    constructor(initial?: Partial<PendingGroupRequest>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

export class PendingGroupResponse extends Mk2Response {
    message?: string;
    ok?: boolean;
    pendingGroup?: PendingGroup;

    constructor(initial?: Partial<PendingGroupResponse>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

@Injectable({ providedIn: 'root' })
export class GetPendingGroupService extends ApiService<PendingGroupRequest, PendingGroupResponse> {
    public requestType = PendingGroupRequest;
    public responseType = PendingGroupResponse;
    public endpointName = 'supplier/PendingGroup/GetPendingGroup';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'supplier/PendingGroup/GetPendingGroup';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(req: PendingGroupRequest): { query: string } | PendingGroupRequest {
        if (this._isPayLaterEnabled) {
            const input: PayablesInput = {
                pendingCreditMemosOnly: true,
                pagination: {
                    skip: 0,
                    take: Number.MAX_SAFE_INTEGER,
                    sortColumn: new EnumInput(PayablesSortColumn.NAME),
                    sortDirection: new EnumInput(SortDirection.ASCENDING)
                }
            };
            const amount = { cents: true, currencyCode: true };
            return {
                query: `{${jsonToGraphQLQuery({
                    payables: {
                        __args: { input },
                        rows: {
                            amount,
                            id: true,
                            name: true,
                            dueInDays: true,
                            creditMemos: {
                                id: true,
                                date: true,
                                amount,
                                allocatedAmount: {
                                    cents: true,
                                    currencyCode: true
                                },
                                creditMemoNumber: true
                            },
                            approvals: {
                                amount,
                                id: true,
                                statusLegacy: true,
                                adjustedDueDate: true,
                                invoice: {
                                    date: true,
                                    terms: true,
                                    invoiceNumber: true
                                }
                            }
                        }
                    }
                } as Query)}}`
            };
        }
        return req;
    }

    public responseMapper(
        res: GraphQLResponse<PayablesResponse> | PendingGroupResponse
    ): PendingGroupResponse {
        if (this._isPayLaterEnabled) {
            const { rows: payables } = (res as GraphQLResponse<PayablesResponse>).data.payables;

            if (!payables.length) {
                return new PendingGroupResponse({
                    ok: true
                });
            }

            const payable = payables[0];

            let payableAmount = payable.amount;
            let deduplicatedCreditMemos = payable.creditMemos;

            // All payables will be pending groups but check for memos just in case
            if (payable.creditMemos.length > 0) {
                deduplicatedCreditMemos = deDuplicatePendingCreditMemos(payable.creditMemos);
                payableAmount = calculateAmount(deduplicatedCreditMemos);
            }

            return new PendingGroupResponse({
                ok: true,
                pendingGroup: <PendingGroup>{
                    groupId: payable.name,
                    groupKey: payable.id,
                    items: [
                        ...deduplicatedCreditMemos.map((creditMemo) => {
                            return <SupplierPendingGroupItem>{
                                itemKey: creditMemo.id.toString(),
                                status: 'CREDIT_MEMO',
                                invoiceDate: creditMemo.date,
                                invoiceNumber: creditMemo.creditMemoNumber,
                                invoiceAmount: creditMemo.amount,
                                dueIn: payable.dueInDays,
                                dueDate: payable.adjustedDueDate
                            };
                        }),
                        ...payable.approvals.map(
                            (approval) =>
                                <SupplierPendingGroupItem>{
                                    itemKey: approval.id.toString(),
                                    status: approval.statusLegacy,
                                    invoiceDate: approval.invoice.date,
                                    invoiceNumber: approval.invoice.invoiceNumber,
                                    invoiceAmount: approval.amount,
                                    dueIn: approval.invoice.terms,
                                    dueDate: approval.adjustedDueDate
                                }
                        )
                    ],
                    total: payableAmount
                }
            });
        }
        return res as PendingGroupResponse;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
